import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPinned, Stethoscope, HandHeart, Leaf, TrendingUp } from "lucide-react";
import { PageHeader } from "../components/PageHeader";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55 },
};

const GROWTH = [
  { icon: MapPinned, title: "Pan-India Presence", text: "From a strong Eastern India base to a nationwide distribution and medical representative network." },
  { icon: Stethoscope, title: "New Therapy Segments", text: "Expanding into cardio-diabetic, geriatric care, palliative care and dermatology formulations." },
  { icon: HandHeart, title: "Affordable Access", text: "Bridging the gap between high-end medical breakthroughs and affordability for every patient." },
  { icon: Leaf, title: "Value & Ethics", text: "Growth driven by integrity and transparent, patient-centric healthcare practices — in all respect." },
];

const MILESTONES = [
  { year: "2007", text: "Founded in Kolkata — first branded oncology molecule house in Eastern India" },
  { year: "2015", text: "Established leadership in oncology, biosimilars & injectable antibiotics" },
  { year: "2022", text: "Expanded into gastroenterology, antibiotics & nutraceutical portfolios" },
  { year: "2026", text: "Cardio-diabetic, geriatric, palliative & dermatology segments underway" },
  { year: "2030", text: "Among the largest pharma houses in Eastern India, with pan-India reach" },
];

const Vision2030 = () => (
  <div data-testid="vision-page">
    <PageHeader
      testId="vision-header"
      eyebrow="Vision 2030"
      title="One of Eastern India's largest pharma houses — by 2030"
      subtitle="A clear destination: a pan-India presence delivering accessible, affordable and ethical healthcare to every corner of the country."
    />

    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="vision-growth-grid">
        {GROWTH.map((g, i) => (
          <motion.div
            key={g.title}
            {...fadeUp}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm transition-shadow duration-300 hover:shadow-xl"
            data-testid={`vision-pillar-${i}`}
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-[#1D4ED8]">
              <g.icon size={20} />
            </div>
            <h3 className="mt-5 text-lg font-bold text-slate-900">{g.title}</h3>
            <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{g.text}</p>
          </motion.div>
        ))}
      </div>

      {/* Roadmap */}
      <div className="mt-24" data-testid="vision-roadmap">
        <motion.div {...fadeUp} className="mb-12 max-w-2xl">
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#1D4ED8]">The Road Ahead</p>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Milestones on the road to 2030
          </h2>
        </motion.div>
        <div className="grid gap-4 md:grid-cols-5">
          {MILESTONES.map((m, i) => (
            <motion.div
              key={m.year}
              {...fadeUp}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className={`rounded-2xl border p-6 ${
                m.year === "2030"
                  ? "border-[#1D4ED8] bg-[#0A1633] text-white"
                  : "border-slate-200 bg-white"
              }`}
              data-testid={`milestone-${m.year}`}
            >
              <div className="flex items-center gap-2">
                <TrendingUp size={16} className={m.year === "2030" ? "text-blue-300" : "text-[#1D4ED8]"} />
                <p className={`font-mono text-sm font-bold tracking-widest ${m.year === "2030" ? "text-blue-300" : "text-[#1D4ED8]"}`}>
                  {m.year}
                </p>
              </div>
              <p className={`mt-3 text-sm leading-relaxed ${m.year === "2030" ? "text-slate-200" : "text-slate-600"}`}>
                {m.text}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      <motion.div {...fadeUp} className="mt-20 text-center">
        <p className="text-lg font-semibold text-slate-800">Join us on this journey — as a partner, professional or distributor.</p>
        <Link
          to="/contact"
          data-testid="vision-contact-button"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#1D4ED8] px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#173CAE]"
        >
          Connect with us <ArrowRight size={16} />
        </Link>
      </motion.div>
    </section>
  </div>
);

export default Vision2030;
